// src/components/common/AppAlert.js
// 78차 39차: 신규 — 앱 전체가 쓰던 React Native 기본 Alert.alert()가 OS 네이티브 팝업이라
// 흰 배경+시스템 폰트로만 뜨고 앱의 다크/골드 톤을 전혀 안 따르는 문제("알림 디자인이 너무
// 기본 같다")를 해결하기 위한 공용 대체 컴포넌트. Alert.alert와 동일한 시그니처
// (title, message, buttons[{text,onPress,style}]) 로 만들어 기존 호출부를 거의 그대로
// (Alert.alert → showAlert) 치환할 수 있게 함.
//
// 사용법: App.js 최상단(NavigationContainer와 형제)에 <AppAlertHost/> 딱 한 번 마운트해두면,
// 이후 어느 파일에서든 showAlert('제목', '내용', [{text:'확인'}]) 호출만으로 뜬다 —
// 실제 렌더는 이 훅 하나가 전담(모듈 스코프 변수로 표시 함수를 공유하는 싱글턴 패턴,
// Alert.alert처럼 命令형으로 호출해야 해서 Context보다 이 방식이 훨씬 단순함).
//
// Alert.prompt(텍스트 입력)는 흉내내지 않음 — StoreAdminScreen.js의 금액입력 1곳만 쓰고
// 있어 이번 교체 대상에서 제외(그 파일은 계속 네이티브 Alert.prompt 사용).
import React, { useState, useEffect, useRef } from 'react';
import { Modal, View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { COLORS, FONTS } from '../../constants/theme';

let _show = null;

export function showAlert(title, message, buttons = [{ text: '확인' }]) {
  if (_show) _show({ title, message, buttons });
  else console.warn('[AppAlert] AppAlertHost가 아직 마운트되지 않았어요:', title);
}

// 79차: [버그 수정] 이미 알림이 떠 있는 상태에서 showAlert()가 또 호출되면(비동기 콜백/
// 포커스 이펙트 등에서 겹칠 수 있음) setState가 그냥 덮어써서 먼저 뜬 알림의 버튼과
// onPress 콜백이 아무 반응 없이 통째로 사라졌음(전수 정독으로 발견) — 네이티브
// Alert.alert()는 여러 개를 스택으로 쌓는데 이 컴포넌트는 그렇지 않았음. 큐를 추가해
// 먼저 뜬 알림이 닫힌 뒤에 다음 알림이 이어서 뜨도록 함.
export default function AppAlertHost() {
  const [state, setState] = useState(null);
  const stateRef = useRef(null);
  const queueRef = useRef([]);

  const showNext = (payload) => {
    stateRef.current = payload;
    setState(payload);
  };

  useEffect(() => {
    _show = (payload) => {
      if (stateRef.current) queueRef.current.push(payload);
      else showNext(payload);
    };
    return () => { _show = null; };
  }, []);

  if (!state) return null;
  const { title, message, buttons } = state;

  const handlePress = (btn) => {
    stateRef.current = null;
    setState(null);
    btn?.onPress?.();
    if (queueRef.current.length > 0) {
      const next = queueRef.current.shift();
      setTimeout(() => showNext(next), 250); // 페이드 아웃/인 애니메이션 겹침 방지
    }
  };

  // 79차: [버그 수정] 항상 buttons[0]을 눌린 것으로 처리하고 있어서, 취소 버튼이
  // 관례상(이 코드베이스의 모든 호출부가 그렇게 씀) 배열 맨 앞이라 우연히 안전했을
  // 뿐 강제되진 않았음 — style:'cancel' 버튼을 명시적으로 찾아서 뒤로가기/Escape
  // 시 항상 그 버튼이 눌리도록(없으면 기존처럼 buttons[0] 폴백).
  const cancelBtn = buttons.find(b => b.style === 'cancel') || buttons[0];

  return (
    <Modal transparent visible animationType="fade" onRequestClose={() => handlePress(cancelBtn)}>
      <View style={s.overlay}>
        <View style={s.box}>
          {!!title && <Text style={s.title}>{title}</Text>}
          {!!message && <Text style={s.message}>{message}</Text>}
          <View style={buttons.length > 2 ? s.btnCol : s.btnRow}>
            {buttons.map((b, i) => {
              const isDestructive = b.style === 'destructive';
              const isCancel = b.style === 'cancel';
              return (
                <TouchableOpacity
                  key={i}
                  activeOpacity={0.8}
                  style={[
                    s.btn,
                    isDestructive && s.btnDestructive,
                    !isDestructive && !isCancel && s.btnPrimary,
                    isCancel && s.btnCancel,
                    buttons.length <= 2 ? { flex: 1 } : { width: '100%' },
                  ]}
                  onPress={() => handlePress(b)}>
                  <Text style={[
                    s.btnText,
                    isDestructive && s.btnTextDestructive,
                    !isDestructive && !isCancel && s.btnTextPrimary,
                    isCancel && s.btnTextCancel,
                  ]}>{b.text}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>
      </View>
    </Modal>
  );
}

const s = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'center', alignItems: 'center', padding: 32 },
  box: { width: '100%', maxWidth: 340, backgroundColor: COLORS.surface, borderRadius: 16, borderWidth: 1, borderColor: COLORS.goldBorder, padding: 22 },
  title: { color: COLORS.text, fontSize: 16, fontWeight: '800', fontFamily: FONTS.bodyBold, marginBottom: 8, textAlign: 'center' },
  message: { color: COLORS.textSec, fontSize: 13.5, lineHeight: 20, textAlign: 'center', marginBottom: 20 },
  btnRow: { flexDirection: 'row', gap: 10 },
  btnCol: { flexDirection: 'column', gap: 10 },
  btn: { paddingVertical: 12, borderRadius: 10, alignItems: 'center', justifyContent: 'center' },
  btnPrimary: { backgroundColor: COLORS.gold },
  btnDestructive: { backgroundColor: 'transparent', borderWidth: 1, borderColor: COLORS.red },
  btnCancel: { backgroundColor: COLORS.surface2, borderWidth: 1, borderColor: COLORS.border },
  btnText: { fontSize: 14, fontWeight: '700' },
  btnTextPrimary: { color: COLORS.onGold },
  btnTextDestructive: { color: COLORS.red },
  btnTextCancel: { color: COLORS.textMuted },
});
